import type {
  QueryResult,
  RangedFieldTypes,
} from '@dynatrace-sdk/client-query';

import { checkTimeseriesFormat } from '../timeseries/check-timeseries-format.js';

/**
 * @public
 */
export interface VisualizationRecommendersProps {
  readonly kind: VisualizationKind;
  readonly recommender: (
    records: QueryResult['records'],
    fieldTypes: RangedFieldTypes[],
  ) => VisualizationRecommenderResult;
}

/**
 * @public
 */
export const VisualizationRecommenders: readonly VisualizationRecommendersProps[] =
  [
    {
      kind: 'Raw',
      recommender: (records) => {
        const rating = records.length > 0 ? 1 : 0;
        return { rating };
      },
    },
    {
      /**
       * Definition for tabular data:
       * - no empty data
       * - all fields definitions must be the same
       */
      kind: 'Table',
      recommender: (records, fieldTypes) => {
        const canDisplayTable = fieldTypes.length > 1;
        if (canDisplayTable) {
          return { rating: 0 };
        }
        const defaultRating = records.length > 0 ? 5 : 0;
        return {
          rating: defaultRating,
        };
      },
    },
    {
      kind: 'TimeSeriesChart',
      recommender: (records, fieldTypes) => {
        const isTimeseriesFormat = fieldTypes.every(
          (type) => checkTimeseriesFormat(type.mappings).isTimeseries,
        );

        const canDisplayTimeSeriesChart = fieldTypes.every((fieldType) => {
          let hasTimestamp = false;
          let hasNumericValue = false;

          Object.keys(fieldType.mappings).forEach((key) => {
            const type = fieldType.mappings[key]?.type;
            if (!type) {
              return;
            }

            if (!hasTimestamp) {
              hasTimestamp = ['timestamp', 'timeframe'].includes(type);
            }

            if (!hasNumericValue) {
              hasNumericValue = ['double', 'long', 'duration'].includes(type);
            }
          });

          return hasTimestamp && hasNumericValue;
        });

        const defaultRating = records.length > 0 ? 9 : 0;

        if (canDisplayTimeSeriesChart || isTimeseriesFormat) {
          return { rating: defaultRating };
        } else {
          return { rating: -1 };
        }
      },
    },

    /* This is where you can add more recommenders */
  ] as const;

/**
 * @public
 */
export const VisualizationKindOptions = [
  'Raw',
  'Table',
  'TimeSeriesChart',
] as const;

/**
 * @public
 */
export type VisualizationKind = (typeof VisualizationKindOptions)[number];

/**
 * @public
 */
export type VisualizationRecommenderResult = {
  rating: number /* higher is better. 0 or less means not recommended */;
};
